import {
  ClipboardCheck,
  ShieldCheck,
  DollarSign,
  Users2,
  Scale,
} from "lucide-react";
import type { BlogPost } from "../types";

export const tenantScreeningProcessExplained: BlogPost = {
  slug: "tenant-screening-process-explained",
  title:
    "Tenant Screening Process Explained: What Rental Property Owners Should Know",
  excerpt:
    "Finding someone willing to rent your property isn't difficult. Finding a qualified tenant requires a careful, documented, and legally compliant screening process.",
  date: "Aug 30, 2026",
  category: "Property Management",
  readTime: "6 min read",
  author: {
    name: "Feingold Property Management Team",
    location: "Laurel, MD & Mid-Atlantic Region",
    initials: "FP",
  },
  intro: [
    "Finding someone willing to rent your property isn't difficult.",
    "Finding a qualified tenant who meets established rental criteria requires a much more careful process.",
    "For landlords and rental property owners throughout Maryland, Washington DC, and Northern Virginia, tenant screening is one of the most important stages of residential property management.",
    "Here's what a professional screening process can involve.",
  ],
  sections: [
    {
      number: "1",
      icon: ClipboardCheck,
      title: "Step 1: Establish Rental Criteria",
      paragraphs: [
        "Tenant screening should begin before applications arrive.",
        "Property owners or their property management company should establish documented rental criteria that comply with applicable federal, state, and local housing requirements.",
        "Criteria may address income, rental history, credit history, occupancy requirements, and other legally permissible factors.",
      ],
      callout: {
        text: "Having established standards also helps create a more consistent application process.",
        variant: "accent",
      },
    },
    {
      number: "2",
      icon: ShieldCheck,
      title: "Step 2: Verify Identity",
      paragraphs: [
        "A rental application contains sensitive information and should be handled carefully.",
        "Identity verification can help establish that an applicant is who they claim to be and reduce the risk of fraudulent applications.",
      ],
      callout: {
        text: "This has become increasingly important as more of the rental process has moved online.",
        variant: "accent",
      },
    },
    {
      number: "3",
      icon: DollarSign,
      title: "Step 3: Review Financial Qualifications",
      paragraphs: [
        "Credit information can provide useful insight, but tenant screening shouldn't necessarily stop at a credit score.",
        "Depending on applicable laws and screening policies, property managers may evaluate income, employment, debt obligations, payment history, and other financial information.",
        "The objective is to understand whether the applicant meets the property's established financial criteria.",
      ],
    },
    {
      number: "4",
      icon: Users2,
      title: "Step 4: Verify Rental History",
      paragraphs: [
        "Previous rental history can provide additional context.",
        "When legally permissible, verification may include previous addresses and communication with prior landlords.",
        "Property managers may seek information about payment history, lease compliance, and other relevant rental experiences.",
      ],
      callout: {
        text: "No screening method can predict exactly how someone will behave, but verification provides owners with more information for making consistent decisions.",
        variant: "accent",
      },
    },
    {
      number: "5",
      icon: Scale,
      title: "Step 5: Follow Applicable Housing Laws",
      paragraphs: [
        "This part matters enormously.",
        "Tenant screening in Maryland, Washington DC, and Virginia must comply with applicable federal, state, and local requirements.",
        "Fair housing protections, application procedures, screening practices, and notices can vary by jurisdiction.",
      ],
      callout: {
        text: "That's one reason local property management experience is valuable.",
        variant: "accent",
      },
    },
  ],
  conclusion: {
    title: "Better Screening Creates a Better Foundation",
    paragraphs: [
      "Tenant screening isn't about finding a 'perfect' tenant.",
      "It's about creating a consistent, documented, legally compliant process for evaluating applicants against established rental criteria.",
      "For owners considering professional rental property management in Maryland, DC, or Northern Virginia, ask potential property managers exactly how their tenant screening process works.",
    ],
    highlight:
      "The tenant relationship begins with the application. A strong process helps start that relationship on solid ground.",
  },
};
